import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Shield, Loader2, Save, CheckCircle2, XCircle, AlertTriangle, Trash2, TestTube } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const PROVIDERS = [
  { id: 'microsoft', name: 'Microsoft (Azure AD / Entra ID)', type: 'oidc', icon: '🏢' },
  { id: 'okta', name: 'Okta', type: 'oidc', icon: '🔐' },
  { id: 'google_workspace', name: 'Google Workspace', type: 'oidc', icon: '🔵' },
  { id: 'onelogin', name: 'OneLogin', type: 'oidc', icon: '🔑' },
  { id: 'custom_oidc', name: 'Custom OIDC Provider', type: 'oidc', icon: '⚙️' },
  { id: 'custom_saml', name: 'Custom SAML 2.0', type: 'saml', icon: '📋' },
];

const ROLES = [
  { id: 'viewer', name: 'Viewer (read-only)' },
  { id: 'property_manager', name: 'Property Manager' },
  { id: 'admin', name: 'Administrator' },
];

export default function SsoSettings({ token }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    provider: '', enabled: false,
    client_id: '', client_secret: '', issuer_url: '', authorization_url: '', token_url: '',
    idp_entity_id: '', idp_sso_url: '', idp_certificate: '',
    domain_hint: '', auto_provision: true, default_role: 'viewer', enforce_sso: false, notes: '',
  });
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/admin/sso-config`, { headers })
      .then(r => {
        setConfig(r.data);
        if (r.data.configured) {
          setForm(prev => ({ ...prev, ...r.data }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/admin/sso-config`, form, { headers });
      toast.success('SSO configuration saved');
      setTestResult(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/admin/sso-config/test`, { headers });
      setTestResult(res.data);
      if (res.data.valid) toast.success('Configuration looks valid!');
      else toast.error(`Issues found: ${res.data.errors.join(', ')}`);
    } catch { toast.error('Failed to test'); }
  };

  const remove = async () => {
    if (!window.confirm('Remove SSO configuration? Users will fall back to email/password login.')) return;
    try {
      await axios.delete(`${BACKEND}/api/admin/sso-config`, { headers });
      toast.success('SSO removed');
      setConfig({ configured: false });
      setForm({ provider: '', enabled: false, client_id: '', client_secret: '', issuer_url: '', authorization_url: '', token_url: '', idp_entity_id: '', idp_sso_url: '', idp_certificate: '', domain_hint: '', auto_provision: true, default_role: 'viewer', enforce_sso: false, notes: '' });
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const selectedProvider = PROVIDERS.find(p => p.id === form.provider);
  const isOidc = selectedProvider?.type === 'oidc';
  const isSaml = selectedProvider?.type === 'saml';

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="sso-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Enterprise SSO</h2>
          <p className="text-sm text-muted-foreground">Configure single sign-on for your organization. Supports Microsoft, Okta, Google Workspace, and custom SAML/OIDC.</p>
        </div>
        {config?.configured && <Badge className={form.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{form.enabled ? 'Active' : 'Disabled'}</Badge>}
      </div>

      {/* Provider Selection */}
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-semibold">Identity Provider</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {PROVIDERS.map(p => (
              <button key={p.id} onClick={() => setForm(prev => ({ ...prev, provider: p.id }))}
                className={`p-3 rounded-lg border text-left transition-all ${form.provider === p.id ? 'shadow-sm' : 'hover:bg-muted/60'}`}
                style={form.provider === p.id ? { borderColor: 'var(--wl-primary, #4F6DF5)', backgroundColor: 'color-mix(in srgb, var(--wl-primary, #4F6DF5) 5%, transparent)' } : undefined}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{p.icon}</span>
                  <div>
                    <span className="text-xs font-semibold block">{p.name}</span>
                    <Badge variant="outline" className="text-[8px] mt-0.5">{p.type.toUpperCase()}</Badge>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {form.provider && (
          <>
            <Separator />

            {/* OIDC Configuration */}
            {isOidc && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">OIDC Configuration</h3>
                <p className="text-xs text-muted-foreground">
                  {form.provider === 'microsoft' && 'Register an app in Azure Portal → App registrations → New registration. Set redirect URI to your LodgiFlow domain + /auth/sso/callback'}
                  {form.provider === 'okta' && 'In Okta Admin → Applications → Create App Integration → OIDC. Set sign-in redirect URI to your LodgiFlow domain + /auth/sso/callback'}
                  {form.provider === 'google_workspace' && 'Go to Google Cloud Console → APIs & Services → Credentials → Create OAuth 2.0 Client ID'}
                  {form.provider === 'onelogin' && 'In OneLogin Admin → Applications → Add App → OIDC. Configure redirect URI.'}
                  {form.provider === 'custom_oidc' && 'Enter your OIDC provider details below.'}
                </p>
                <div><Label className="text-xs">Client ID</Label><Input value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))} placeholder="your-client-id" className="mt-1 font-mono text-xs" data-testid="sso-client-id" /></div>
                <div><Label className="text-xs">Client Secret</Label><Input type="password" value={form.client_secret} onChange={e => setForm(p => ({ ...p, client_secret: e.target.value }))} placeholder="your-client-secret" className="mt-1 font-mono text-xs" /></div>
                <div><Label className="text-xs">Issuer URL</Label><Input value={form.issuer_url} onChange={e => setForm(p => ({ ...p, issuer_url: e.target.value }))} placeholder={form.provider === 'microsoft' ? 'https://login.microsoftonline.com/{tenant-id}/v2.0' : form.provider === 'okta' ? 'https://your-org.okta.com' : 'https://...'} className="mt-1 font-mono text-xs" /></div>
                {form.provider === 'custom_oidc' && (
                  <>
                    <div><Label className="text-xs">Authorization URL</Label><Input value={form.authorization_url} onChange={e => setForm(p => ({ ...p, authorization_url: e.target.value }))} placeholder="https://provider.com/authorize" className="mt-1 font-mono text-xs" /></div>
                    <div><Label className="text-xs">Token URL</Label><Input value={form.token_url} onChange={e => setForm(p => ({ ...p, token_url: e.target.value }))} placeholder="https://provider.com/token" className="mt-1 font-mono text-xs" /></div>
                  </>
                )}
              </div>
            )}

            {/* SAML Configuration */}
            {isSaml && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">SAML 2.0 Configuration</h3>
                <p className="text-xs text-muted-foreground">Enter the details from your identity provider's SAML metadata.</p>
                <div><Label className="text-xs">IdP Entity ID</Label><Input value={form.idp_entity_id} onChange={e => setForm(p => ({ ...p, idp_entity_id: e.target.value }))} placeholder="https://idp.example.com/entity" className="mt-1 font-mono text-xs" /></div>
                <div><Label className="text-xs">IdP SSO URL</Label><Input value={form.idp_sso_url} onChange={e => setForm(p => ({ ...p, idp_sso_url: e.target.value }))} placeholder="https://idp.example.com/sso" className="mt-1 font-mono text-xs" /></div>
                <div><Label className="text-xs">IdP Certificate (PEM)</Label><Textarea value={form.idp_certificate} onChange={e => setForm(p => ({ ...p, idp_certificate: e.target.value }))} placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----" className="mt-1 font-mono text-[10px] min-h-[100px]" /></div>
              </div>
            )}

            <Separator />

            {/* Behavior Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Behavior</h3>
              <div><Label className="text-xs">Email Domain (for auto-matching)</Label><Input value={form.domain_hint} onChange={e => setForm(p => ({ ...p, domain_hint: e.target.value }))} placeholder="coastalhospitality.com" className="mt-1" /><p className="text-[10px] text-muted-foreground mt-0.5">Users with this email domain will be prompted to use SSO</p></div>
              <div><Label className="text-xs">Default Role for New SSO Users</Label>
                <Select value={form.default_role} onValueChange={v => setForm(p => ({ ...p, default_role: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><span className="text-sm font-medium">Auto-Provision Users</span><p className="text-[10px] text-muted-foreground">Automatically create accounts for new SSO users</p></div>
                <Switch checked={form.auto_provision} onCheckedChange={v => setForm(p => ({ ...p, auto_provision: v }))} />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><span className="text-sm font-medium">Enforce SSO</span><p className="text-[10px] text-muted-foreground">Block email/password login — require SSO for all users</p></div>
                <Switch checked={form.enforce_sso} onCheckedChange={v => setForm(p => ({ ...p, enforce_sso: v }))} />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><span className="text-sm font-medium">Enable SSO</span><p className="text-[10px] text-muted-foreground">Turn on SSO login for your organization</p></div>
                <Switch checked={form.enabled} onCheckedChange={v => setForm(p => ({ ...p, enabled: v }))} />
              </div>
            </div>

            {/* Test result */}
            {testResult && (
              <div className={`p-3 rounded-lg border ${testResult.valid ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-2 text-sm font-medium">{testResult.valid ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}{testResult.valid ? 'Configuration valid' : 'Configuration issues'}</div>
                {testResult.errors?.length > 0 && <ul className="mt-1 text-xs text-red-700">{testResult.errors.map((e, i) => <li key={i}>• {e}</li>)}</ul>}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={save} disabled={saving} className="gap-1">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Configuration
              </Button>
              <Button variant="outline" onClick={test} className="gap-1">
                <TestTube className="h-3.5 w-3.5" /> Validate
              </Button>
              {config?.configured && (
                <Button variant="outline" onClick={remove} className="gap-1 text-destructive hover:text-destructive ml-auto">
                  <Trash2 className="h-3.5 w-3.5" /> Remove SSO
                </Button>
              )}
            </div>
          </>
        )}

        {/* Info box */}
        <div className="p-4 rounded-lg border bg-blue-50/50 border-blue-200/50">
          <h4 className="text-xs font-semibold text-blue-800 mb-1">How Enterprise SSO Works</h4>
          <ol className="text-[10px] text-blue-700/80 space-y-1 list-decimal list-inside">
            <li>Choose your identity provider above</li>
            <li>Register LodgiFlow in your IdP's admin console</li>
            <li>Enter the credentials and URLs from your IdP</li>
            <li>Click Validate to check the configuration</li>
            <li>Enable SSO — your team can now sign in with their corporate credentials</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
